import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useT } from '../i18n';
import { searchPlaces, shortLabel, type GeocodeResult } from '../lib/nominatim';
import './LocalitySearch.css';

interface LocalitySearchProps {
  onSelect: (result: GeocodeResult) => void;
  /** Overrides the default map-search label, so the manual route can reuse this box. */
  label?: string;
  placeholder?: string;
  /** Seeds the box when returning to the step with something already typed. */
  initialQuery?: string;
  /**
   * Fires on every keystroke, not only on a pick. The manual route needs the raw text
   * kept even when nobody selects a suggestion — a typed address is the only record of
   * where that parcel is.
   */
  onQueryChange?: (value: string) => void;
}

type Status = 'idle' | 'loading' | 'error';

const DEBOUNCE_MS = 450;
const MIN_QUERY = 3;

export function LocalitySearch({ onSelect, label, placeholder, initialQuery, onQueryChange }: LocalitySearchProps) {
  const t = useT();
  const listboxId = useId();
  const inputId = useId();

  const [query, setQuery] = useState(initialQuery ?? '');
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const abortRef = useRef<AbortController | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  // Set when a result is chosen, so the resulting setQuery does not immediately
  // fire a fresh search for the text we just filled in.
  const suppressRef = useRef(false);

  useEffect(() => {
    if (suppressRef.current) {
      suppressRef.current = false;
      return;
    }

    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY) {
      abortRef.current?.abort();
      setResults([]);
      setStatus('idle');
      setOpen(false);
      return;
    }

    const timer = setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setStatus('loading');
      searchPlaces(trimmed, controller.signal)
        .then((found) => {
          if (controller.signal.aborted) return;
          setResults(found);
          setStatus('idle');
          setOpen(true);
          setActiveIndex(found.length > 0 ? 0 : -1);
        })
        .catch((error: unknown) => {
          if (error instanceof DOMException && error.name === 'AbortError') return;
          setStatus('error');
          setResults([]);
          setOpen(true);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => () => abortRef.current?.abort(), []);

  // Dismiss the listbox on an outside click.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const choose = useCallback(
    (result: GeocodeResult) => {
      suppressRef.current = true;
      setQuery(shortLabel(result.label));
      onQueryChange?.(shortLabel(result.label));
      setOpen(false);
      setActiveIndex(-1);
      onSelect(result);
    },
    [onSelect, onQueryChange],
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open || results.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      const picked = results[activeIndex];
      if (picked) {
        event.preventDefault();
        choose(picked);
      }
    }
  };

  const clear = () => {
    suppressRef.current = false;
    setQuery('');
    onQueryChange?.('');
    setResults([]);
    setOpen(false);
    setStatus('idle');
  };

  const showEmpty = open && status === 'idle' && results.length === 0 && query.trim().length >= MIN_QUERY;

  return (
    <div className="locality-search" ref={rootRef}>
      <label className="field__label" htmlFor={inputId}>
        {label ?? t.step1.searchLabel}
      </label>

      <div className="locality-search__control">
        <input
          id={inputId}
          className="input locality-search__input"
          type="text"
          role="combobox"
          autoComplete="off"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 && results[activeIndex] ? `${listboxId}-${activeIndex}` : undefined
          }
          placeholder={placeholder ?? t.step1.searchPlaceholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); onQueryChange?.(e.target.value); }}
          onKeyDown={onKeyDown}
          onFocus={() => {
            if (results.length > 0) setOpen(true);
          }}
        />
        {query.length > 0 && (
          <button
            type="button"
            className="locality-search__clear"
            onClick={clear}
            aria-label={t.step1.searchClear}
          >
            <span aria-hidden="true">×</span>
          </button>
        )}
      </div>

      <p className="field__help">{t.step1.searchHint}</p>

      {/* Status is announced politely so a screen reader learns the search ran
          without stealing focus from the input mid-typing. */}
      <span className="visually-hidden" role="status" aria-live="polite">
        {status === 'loading' ? t.step1.searching : ''}
      </span>

      {open && (
        <ul className="locality-search__results" id={listboxId} role="listbox">
          {status === 'error' && <li className="locality-search__message">{t.step1.searchError}</li>}
          {showEmpty && <li className="locality-search__message">{t.step1.searchNoResults}</li>}
          {results.map((result, index) => (
            <li key={result.id} role="presentation">
              <button
                type="button"
                id={`${listboxId}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={
                  index === activeIndex
                    ? 'locality-search__option locality-search__option--active'
                    : 'locality-search__option'
                }
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => choose(result)}
              >
                {shortLabel(result.label)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
